import type { ReactNode } from 'react';
import { useDrawers, type DrawerMember } from '@/contexts/DrawerContext';
import { cn } from '@/lib/utils';

const LINK_CLASS = 'cursor-pointer text-left hover:text-[#4369a0] hover:underline';

function LinkButton({ onClick, className, children }: { onClick: () => void; className?: string; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={cn(LINK_CLASS, className)}
    >
      {children}
    </button>
  );
}

interface RecordLinkProps {
  /** The member the policy or loan belongs to. */
  member: DrawerMember;
  className?: string;
}

/** Policy number that opens Policy Details. */
export function PolicyLink({ member, policyNo, className }: RecordLinkProps & { policyNo: string }) {
  const { openPolicy } = useDrawers();
  return (
    <LinkButton onClick={() => openPolicy(member, policyNo)} className={className}>
      {policyNo}
    </LinkButton>
  );
}

/** Loan number that opens the Policy Loan drawer. */
export function LoanLink({ member, loanNo, className }: RecordLinkProps & { loanNo: string }) {
  const { openLoan } = useDrawers();
  return (
    <LinkButton onClick={() => openLoan(member, loanNo)} className={className}>
      {loanNo}
    </LinkButton>
  );
}

/** A policy or loan number (loan numbers start with "L"); "--" when empty. */
export function RecordLink({ member, refNo, className }: RecordLinkProps & { refNo?: string | null }) {
  if (!refNo) return <span className="text-gray-400">--</span>;
  return refNo.startsWith('L') ? (
    <LoanLink member={member} loanNo={refNo} className={className} />
  ) : (
    <PolicyLink member={member} policyNo={refNo} className={className} />
  );
}

/** Member name that opens View Member. */
export function MemberLink({
  memberId,
  memberNo,
  name,
  className,
}: {
  memberId: string;
  /** member_id, shown in the drawer header. */
  memberNo?: string;
  name: string;
  className?: string;
}) {
  const { openDrawer } = useDrawers();
  return (
    <LinkButton onClick={() => openDrawer('viewMember', { id: memberId, name, memberNo })} className={className}>
      {name}
    </LinkButton>
  );
}

/** Card number cell text; "--" when the policy has none. */
export function CardNo({ value }: { value?: string }) {
  return value ? <span className="tabular-nums">{value}</span> : <span className="text-gray-400">--</span>;
}
